import { useState, useRef, useCallback } from 'react';
import type {
  ImportPreviewResult,
  ImportWithMappingResult,
  ImportServiceAdapter,
  MappingColumnConfig,
} from '../types/import';

interface UseImportWithMappingParams {
  adapter: ImportServiceAdapter;
  columns: MappingColumnConfig[];
  getOptions: () => Record<string, unknown>;
  entityLabel: string;
  templateFilename: string;
  onImportComplete?: () => void;
}

export function useImportWithMapping({
  adapter,
  columns,
  getOptions,
  entityLabel,
  templateFilename,
  onImportComplete,
}: UseImportWithMappingParams) {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [mappings, setMappings] = useState<Record<string, Record<string, string>>>({});
  const [importResult, setImportResult] = useState<ImportWithMappingResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFile(selectedFile);
    setError(null);
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const blob = await adapter.downloadTemplate(getOptions());
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = templateFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Erro ao baixar template');
    } finally {
      setTemplateLoading(false);
    }
  }, [adapter, getOptions, templateFilename]);

  const doImport = useCallback(async (importMappings?: Record<string, Record<string, string | number>>) => {
    if (!file) return;
    setError(null);
    setLoading(true);

    try {
      const result = await adapter.executeImport(file, importMappings, getOptions());
      setImportResult(result);

      const messages: string[] = [];
      if (result.imported > 0) {
        messages.push(`${result.imported} ${entityLabel}(s) importado(s) com sucesso.`);
      }
      if (result.skipped && result.skipped > 0) {
        messages.push(`${result.skipped} registro(s) ignorado(s) (já existem).`);
      }
      if (result.errors.length > 0) {
        const errorLines = result.errors.map(e => `Linha ${e.row}: ${e.error}`).join('\n');
        messages.push(`${result.errors.length} erro(s):\n${errorLines}`);
      }

      if (result.imported > 0) {
        onImportComplete?.();
        if (result.errors.length === 0 && (!result.skipped || result.skipped === 0)) {
          return { shouldClose: true, result };
        }
      }

      if (messages.length > 0 && (result.errors.length > 0 || result.imported === 0)) {
        setError(messages.join('\n'));
      }

      return { shouldClose: false, result };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao importar';
      setError(message);
      return { shouldClose: false, result: null };
    } finally {
      setLoading(false);
    }
  }, [file, adapter, getOptions, entityLabel, onImportComplete]);

  const handlePreview = useCallback(async () => {
    if (!file) { setError('Selecione um arquivo para importar'); return { shouldClose: false }; }
    setError(null);
    setLoading(true);

    try {
      const previewResult = await adapter.previewImport(file, getOptions());
      setPreview(previewResult);

      if (previewResult.unresolvedTerms.length > 0) {
        const initialMappings: Record<string, Record<string, string>> = {};
        for (const term of previewResult.unresolvedTerms) {
          if (!initialMappings[term.column]) initialMappings[term.column] = {};
          initialMappings[term.column][term.term] = '';
        }
        setMappings(initialMappings);
        setActiveStep(1);
        return { shouldClose: false };
      } else {
        const result = await doImport();
        return result ?? { shouldClose: false };
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message
        : (err as Record<string, unknown> & { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Erro ao processar arquivo';
      setError(message);
      return { shouldClose: false };
    } finally {
      setLoading(false);
    }
  }, [file, adapter, getOptions, doImport]);

  const handleConfirmImport = useCallback(async () => {
    const columnMap = new Map(columns.map(c => [c.key, c]));
    const importMappings: Record<string, Record<string, string | number>> = {};

    for (const [columnKey, termMappings] of Object.entries(mappings)) {
      if (!termMappings || Object.keys(termMappings).length === 0) continue;

      const config = columnMap.get(columnKey);
      const converted: Record<string, string | number> = {};

      for (const [term, value] of Object.entries(termMappings)) {
        if (!value) continue;
        converted[term] = config?.valueType === 'number' ? Number(value) : value;
      }

      if (Object.keys(converted).length > 0) {
        importMappings[columnKey] = converted;
      }
    }

    return await doImport(Object.keys(importMappings).length > 0 ? importMappings : undefined);
  }, [columns, mappings, doImport]);

  const setMappingValue = useCallback((column: string, term: string, value: string) => {
    setMappings(prev => ({
      ...prev,
      [column]: { ...prev[column], [term]: value },
    }));
  }, []);

  const allRequiredMapped = useCallback(() => {
    if (!preview) return false;
    return preview.unresolvedTerms
      .filter(t => t.required)
      .every(t => mappings[t.column]?.[t.term]);
  }, [preview, mappings]);

  const handleClose = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setError(null);
    setPreview(null);
    setMappings({});
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return {
    activeStep,
    file,
    loading,
    templateLoading,
    error,
    preview,
    mappings,
    importResult,
    fileInputRef,
    setError,
    handleFileChange,
    handleDownloadTemplate,
    handlePreview,
    handleConfirmImport,
    setMappingValue,
    allRequiredMapped,
    handleClose,
  };
}
